import { Component, ViewChild } from '@angular/core';
import { FormControl, ValidationErrors } from '@angular/forms';
import {
    SIGNAL_BYTE_ORDER_OPTIONS,
    SIGNAL_TYPE_OPTIONS,
    SIGNAL_VALUE_TYPE_OPTIONS,
} from 'src/utils/signal-enum-label.util';
import { SignalAnchor, SignalVersion } from 'src/models/anchor-and-version/signal.model';
import { CrudBaseModalComponent } from '../crud-base-modal/crud-base-modal.component';
import { CrudModalAbstractComponent } from '../crud-base-modal/crud-modal-abstract.component';
import { ModalProps } from '../crud-base-modal/modal-field.interface';
import { SignalValueDescriptionTableComponent } from './crud-signal-value-description-table/signal-value-description-table.component';

@Component({
    selector: 'app-crud-signal-modal',
    template: `
        <app-crud-base-modal #baseModal [modalProps]="modalProps">
            <app-signal-value-description-table
                #valueDescriptionsTable
                (rowsChanged)="onValueDescriptionsChanged()"
            ></app-signal-value-description-table>
        </app-crud-base-modal>
    `,
    imports: [CrudBaseModalComponent, SignalValueDescriptionTableComponent],
})
export class CrudSignalModalComponent extends CrudModalAbstractComponent<
    SignalAnchor,
    SignalVersion
> {
    @ViewChild('valueDescriptionsTable')
    public valueDescriptionsTable?: SignalValueDescriptionTableComponent;

    public modalProps: ModalProps = {
        title: 'Signal',
        validateNameFormat: true,
        afterSave: (savedItem?: unknown) => this.saveValueDescriptions(savedItem),
        insertContentAfter: 'unit',
        anchorFields: [],
        versionFields: [
            {
                field: 'changeRequestKey',
                label: 'Change Request',
                type: 'lazy-dropdown',
                required: true,
                order: 1,
            },
            { field: 'name', label: 'Name', type: 'text', required: true, order: 2 },
            { field: 'fullName', label: 'Full Name', type: 'text', order: 3 },
            {
                field: 'signalValueType',
                label: 'Signal Value Type',
                type: 'dropdown',
                order: 4,
                defaultValue: 1,
                options: SIGNAL_VALUE_TYPE_OPTIONS,
            },
            {
                field: 'signalType',
                label: 'Signal Type',
                type: 'dropdown',
                order: 5,
                defaultValue: 0,
                options: SIGNAL_TYPE_OPTIONS,
            },
            {
                field: 'bitSize',
                label: 'Bit Size',
                type: 'number',
                required: true,
                defaultValue: 1,
                order: 6,
            },
            {
                field: 'byteOrder',
                label: 'Byte Order',
                type: 'dropdown',
                order: 7,
                defaultValue: 0,
                options: SIGNAL_BYTE_ORDER_OPTIONS,
            },
            {
                field: 'formulaFactor',
                label: 'Factor',
                type: 'text',
                required: true,
                defaultValue: '1',
                order: 8,
            },
            {
                field: 'formulaOffset',
                label: 'Offset',
                type: 'text',
                required: true,
                defaultValue: '0',
                order: 9,
            },
            {
                field: 'initialValue',
                label: 'Initial Value',
                type: 'text',
                required: true,
                order: 10,
            },
            { field: 'min', label: 'Min', type: 'text', required: true, order: 11 },
            { field: 'max', label: 'Max', type: 'text', required: true, order: 12 },
            {
                field: 'spn',
                label: 'SPN',
                type: 'number',
                required: true,
                defaultValue: 0,
                order: 13,
            },
            { field: 'unit', label: 'Unit', type: 'text', order: 14 },
            {
                field: 'valueTableName',
                label: 'Value Table Name',
                type: 'text',
                defaultValue: '',
                hidden: true,
                order: 15,
            },
            { field: 'signalNote', label: 'Note', type: 'textarea', order: 16 },
            { field: 'comment', label: 'Comment', type: 'textarea', order: 17 },
            { field: 'isActive', label: 'Is Active', type: 'switch', order: 18 },
        ],
    };

    private readonly valueDescriptionsControlName = '__valueDescriptions';

    public override onOpen(copyFromSignalAnchorKey?: number): void {
        void this.openSignalModal(copyFromSignalAnchorKey);
    }

    public onValueDescriptionsChanged(): void {
        if (!this.baseModal) return;

        this.updateValueDescriptionsValidity(true);
        this.baseModal.hasUnsavedChanges = true;
    }

    private getSignalAnchorKey(savedItem?: unknown): number | undefined {
        const anchorKey = (this.modalProps.anchor as Record<string, unknown> | undefined)?.['key'];
        if (typeof anchorKey === 'number') return anchorKey;

        const savedItemKey = (savedItem as Record<string, unknown> | undefined)?.['key'];
        return typeof savedItemKey === 'number' ? savedItemKey : undefined;
    }

    private getValueDescriptionsValidationErrors(): ValidationErrors | null {
        const validationMessage = this.valueDescriptionsTable?.getValidationMessage();
        return validationMessage ? { valueDescriptions: validationMessage } : null;
    }

    private async openSignalModal(copyFromSignalAnchorKey?: number): Promise<void> {
        await this.baseModal?.onOpen();
        this.registerValueDescriptionsControl();
        await this.valueDescriptionsTable?.load(
            this.modalProps.anchor as SignalAnchor | undefined,
            copyFromSignalAnchorKey
        );
        this.updateValueDescriptionsValidity();
    }

    private registerValueDescriptionsControl(): void {
        if (!this.form) return;

        const existingControl = this.form.get(this.valueDescriptionsControlName);
        if (existingControl) {
            existingControl.setValidators(() => this.getValueDescriptionsValidationErrors());
            this.updateValueDescriptionsValidity();
            return;
        }

        this.form.addControl(
            this.valueDescriptionsControlName,
            new FormControl(null, () => this.getValueDescriptionsValidationErrors())
        );
        this.updateValueDescriptionsValidity();
    }

    private async saveValueDescriptions(savedItem?: unknown): Promise<void> {
        const signalAnchorKey = this.getSignalAnchorKey(savedItem);
        const changeRequestKey = Number(this.form?.get('changeRequestKey')?.value ?? 0);

        if (!signalAnchorKey || Number.isNaN(changeRequestKey)) {
            const message =
                'Cannot save value descriptions because signal or change request is missing.';
            throw new Error(message);
        }

        await this.valueDescriptionsTable?.save(signalAnchorKey, changeRequestKey);
    }

    private updateValueDescriptionsValidity(markDirty = false): void {
        const control = this.form?.get(this.valueDescriptionsControlName);
        if (!control) return;

        if (markDirty) {
            control.markAsDirty();
        }
        control.updateValueAndValidity({ emitEvent: false });
    }
}
